import { create } from 'zustand';
import type { Venue } from '../types/venue';
import { venues as initialVenues } from '../data/venues';
import { roundUpToHalfStar } from '../utils/rating';

interface Filters {
  capacity: {
    min: number;
    max: number;
  };
  minRating: number;
  country: string | null;
  region: string | null;
}

interface VenueState {
  venues: Venue[];
  selectedVenue: Venue | null;
  searchQuery: string;
  filters: Filters;
  filteredVenues: Venue[];
  setSelectedVenue: (venue: Venue | null) => void;
  setSearchQuery: (query: string) => void;
  setFilters: (filters: Filters) => void;
  updateFilteredVenues: () => void;
}

interface VenueStoreState {
  showAIAssistant: boolean;
  countryFilter: string | null;
  regionFilter: string | null;
  capacityFilter: number | null;
  ratingFilter: number | null;
}

interface VenueStoreActions {
  setShowAIAssistant: (value: boolean) => void;
  setCountryFilter: (country: string | null) => void;
  setRegionFilter: (region: string | null) => void;
  setCapacityFilter: (capacity: number | null) => void;
  setRatingFilter: (rating: number | null) => void;
}

const initialFilters: Filters = {
  capacity: { min: 0, max: 0 },
  minRating: 0,
  country: null,
  region: null,
};

export const useVenueStore = create<VenueState & VenueStoreState & VenueStoreActions>((set, get) => ({
  venues: initialVenues,
  filteredVenues: initialVenues,
  selectedVenue: null,
  searchQuery: '',
  filters: initialFilters,
  showAIAssistant: false,
  countryFilter: null,
  regionFilter: null,
  capacityFilter: null,
  ratingFilter: null,
  setSelectedVenue: (venue) => set({ selectedVenue: venue }),
  setSearchQuery: (query) => {
    set({ searchQuery: query });
    get().updateFilteredVenues();
  },
  setFilters: (filters) => {
    set({ filters });
    get().updateFilteredVenues();
  },
  setShowAIAssistant: (value: boolean) => set({ showAIAssistant: value }),
  setCountryFilter: (country) => {
    set({ countryFilter: country });
    get().updateFilteredVenues();
  },
  setRegionFilter: (region) => {
    set({ regionFilter: region });
    get().updateFilteredVenues();
  },
  setCapacityFilter: (capacity) => {
    set({ capacityFilter: capacity });
    get().updateFilteredVenues();
  },
  setRatingFilter: (rating) => {
    set({ ratingFilter: rating });
    get().updateFilteredVenues();
  },
  updateFilteredVenues: () => {
    const { venues, filters, searchQuery } = get();
    
    let displayVenues = [...venues];
    
    // Filter by search query first
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      displayVenues = displayVenues.filter(venue => 
        venue.name.toLowerCase().includes(query) || 
        venue.address.toLowerCase().includes(query)
      );
    }
    
    // Filter by country
    if (filters.country) {
      displayVenues = displayVenues.filter(venue => 
        venue.country === filters.country
      );
    }
    
    // Filter by region (if a country is selected)
    if (filters.region) {
      displayVenues = displayVenues.filter(venue => 
        venue.region === filters.region
      );
    }
    
    // Filter by capacity
    if (filters.capacity) {
      displayVenues = displayVenues.filter(venue =>
        venue.capacity.max >= filters.capacity.min
      );
    }
    
    set({ filteredVenues: displayVenues });
  },
}));

// Ensure filters are applied on initial load
setTimeout(() => {
  useVenueStore.getState().updateFilteredVenues();
}, 0);